/**
 * Flotte, invitations et rapports.
 *
 * Toute l'agrégation reste en SQL : le téléphone reçoit des lignes déjà
 * calculées, il ne recompose jamais une indemnité ni un total d'heures.
 */
import { rpc } from '@/lib/http';

export interface FleetDriver {
  id: string;
  full_name: string;
  email: string;
  phone: string | null;
  role: 'driver' | 'fleet_manager' | 'ceo_admin';
  created_at: string;
  trips_30d: number;
  clocked_in: boolean | null;
}

/** Ligne de sp_list_invitations — le token en clair n'y figure jamais. */
export interface Invitation {
  id: string;
  invited_email: string;
  invited_name: string | null;
  invited_by_email: string | null;
  status: 'pending' | 'accepted' | 'expired' | 'revoked';
  created_at: string;
  expires_at: string;
}

/**
 * Retour de sp_create_invitation. Le token en clair ne sort qu'ici, une seule
 * fois : la base n'en garde qu'une empreinte SHA-256. À transmettre au
 * conducteur immédiatement, sinon il faut recréer l'invitation.
 */
export interface CreatedInvitation {
  invitation_id: string;
  token: string;
  /** L'API a-t-elle réussi à envoyer l'email ? Faux si SMTP est absent ou en panne. */
  emailed: boolean;
}

export const fleetService = {
  drivers: () => rpc<FleetDriver[]>('fleet.drivers'),
  monthly: (months = 12) => rpc<Record<string, unknown>[]>('fleet.monthly', { months }),
  invitations: () => rpc<Invitation[]>('fleet.invitations'),
  invite: (email: string, name?: string) => rpc<CreatedInvitation>('invitation.create', { email, name }),
  revokeInvite: (id: string) => rpc<{ revoked: string }>('invitation.revoke', { id }),
  acceptInvite: (token: string) => rpc<{ company_id: string }>('invitation.accept', { token }),
};

export interface Period {
  from: string;
  to: string;
  driverId?: string;
}

export const reportService = {
  allowances: (p: Period) => rpc<Record<string, unknown>[]>('report.allowances', { from: p.from, to: p.to, driver_id: p.driverId }),
  tripDetail: (p: Period) => rpc<Record<string, unknown>[]>('report.trip_detail', { from: p.from, to: p.to, driver_id: p.driverId }),
  timesheet: (p: Period) => rpc<Record<string, unknown>[]>('report.timesheet', { from: p.from, to: p.to, driver_id: p.driverId }),
  hours: (p: Omit<Period, 'driverId'>) => rpc<Record<string, unknown>[]>('report.hours', p),
  autoAccuracy: (p: Omit<Period, 'driverId'>) => rpc<Record<string, unknown>[]>('report.auto_accuracy', p),
  ceoOverview: () => rpc<{ overview: Record<string, unknown> }>('report.ceo_overview'),
};

export interface AllowanceRate {
  id: string;
  country: string;
  valid_from: string;
  valid_to: string;
  rate_per_km: number;
  source: string | null;
}

export const rateService = {
  list: () => rpc<AllowanceRate[]>('rates.list'),
  coverage: () => rpc<Record<string, unknown>[]>('rates.coverage'),

  /**
   * Choix du barème par le conducteur.
   *
   * Refusé par le serveur s'il est rattaché à une société : c'est elle qui
   * paie l'indemnité, c'est elle qui fixe le taux.
   */
  setRate: (p: { mode: 'auto' | 'fixed' | 'custom'; rate_id?: string; rate_per_km?: number }) =>
    rpc<{ rate_mode: string; rate_id: string | null; custom_rate_per_km: number | null }>(
      'profile.setRate', { ...p }
    ),
};

export const planService = {
  get: () => rpc<{ plan: string; status: string; current_period_end: string; quantity: number } | null>('plan.get'),
};

export const supportService = {
  send: (msg: { name?: string; email?: string; subject?: string; message: string }) =>
    rpc<{ received: true }>('support.send', msg),
};
